import { useEffect, useMemo, useRef, useState } from "react";
import { formatDisplayName } from "../utils/displayName";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  CheckCircle2,
  Filter,
  LayoutGrid,
  List,
  Loader2,
  MoreHorizontal,
  Pencil,
  Search,
  Trash2,
  X,
} from "lucide-react";
import AdminDashboardLayout from "../components/AdminDashboardLayout";
import PortalFeedback from "../components/PortalFeedback";
import { supabase } from "../supabaseClient";
import { PROJECT_STATUSES, STATUS_TONE, getStatusSortIndex, normalizeProjectStatus } from "../utils/projectStatus";
import { formatExpectedTiming } from "../utils/timeFormat";

const PANEL =
  "rounded-2xl border border-white/15 bg-white/70 backdrop-blur-md shadow-[0_10px_30px_rgba(0,0,0,0.08)]";

const STATUS_ORDER = PROJECT_STATUSES;
const PRIORITY_ORDER = ["Urgent", "Medium", "Low"];
const DATE_FILTERS = [
  { value: "all", label: "All dates" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "year", label: "This year" },
];
const getParamValue = (params, key, fallback, allowedValues) => {
  const value = params.get(key);
  return allowedValues.includes(value) ? value : fallback;
};
const getPositiveNumberParam = (params, key, fallback, allowedValues = null) => {
  const value = Number(params.get(key));
  if (!Number.isFinite(value) || value < 1) return fallback;
  return allowedValues && !allowedValues.includes(value) ? fallback : value;
};
const getActiveFilterSummary = (filters) => {
  const labels = [];
  if (filters.status !== "all") labels.push(`Status: ${filters.status}`);
  if (filters.priority !== "all") labels.push(`Priority: ${filters.priority}`);
  if (filters.dateRange !== "all") {
    labels.push(DATE_FILTERS.find((item) => item.value === filters.dateRange)?.label || filters.dateRange);
  }
  if (labels.length === 0) return "Filters";
  if (labels.length === 1) return labels[0];
  return `${labels[0]} +${labels.length - 1}`;
};
const getSortLabel = (sort) => {
  const labels = {
    modified: "Last modified",
    requested: "Requested date",
    expected: "Expected date",
    tail: "Tail number",
    broker: "Broker",
    status: "Status",
    priority: "Priority",
  };
  return labels[sort.by] || "Last modified";
};

const normalizeStatus = (status) => normalizeProjectStatus(status);

export default function AdminProjectsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const initialFilters = {
    status: getParamValue(params, "status", "all", ["all", ...STATUS_ORDER]),
    priority: getParamValue(params, "priority", "all", ["all", ...PRIORITY_ORDER]),
    dateRange: getParamValue(params, "date", "all", DATE_FILTERS.map((item) => item.value)),
  };
  const hasActiveInitialFilters = Object.values(initialFilters).some((value) => value !== "all");
  const [viewMode, setViewMode] = useState(getParamValue(params, "view", "list", ["list", "card"]));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorText, setErrorText] = useState("");
  const [q, setQ] = useState(params.get("q") || "");
  const [showFilters, setShowFilters] = useState(params.get("filters") === "1" || hasActiveInitialFilters);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [toast, setToast] = useState(null);
  const [filters, setFilters] = useState(initialFilters);
  const [sort, setSort] = useState({
    by: getParamValue(params, "sort", "modified", ["modified", "requested", "expected", "tail", "broker", "status", "priority"]),
    dir: getParamValue(params, "dir", "desc", ["asc", "desc"]),
  });
  const [page, setPage] = useState(getPositiveNumberParam(params, "page", 1));
  const [pageSize, setPageSize] = useState(getPositiveNumberParam(params, "pageSize", 10, [10, 20, 50]));
  const toastTimerRef = useRef(null);

  const loadRows = async () => {
    setLoading(true);
    setErrorText("");
    const { data, error } = await supabase
      .from("project_status")
      .select(
        "id,intake_id,tail_number,current_status,priority,assigned_pilot,expected_datetime,timezone,updated_at,broker_intake(id,created_at,services,airport_identifier,contact_name,contact_phone,contact_email,contact_method_preference,notes,broker_accounts(name))"
      )
      .order("updated_at", { ascending: false })
      .limit(1000);

    if (error) {
      setErrorText(error.message || "Could not load projects.");
      setRows([]);
      setLoading(false);
      return;
    }

    setRows(data || []);
    setLoading(false);
  };

  useEffect(() => {
    loadRows();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".admin-kebab-menu")) setOpenMenuId(null);
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (location.state?.message) {
      showToast("Updated", location.state.message);
      navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: {} });
    }
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    const next = new URLSearchParams(location.search);
    q ? next.set("q", q) : next.delete("q");
    viewMode !== "list" ? next.set("view", viewMode) : next.delete("view");
    showFilters ? next.set("filters", "1") : next.delete("filters");
    filters.status !== "all" ? next.set("status", filters.status) : next.delete("status");
    filters.priority !== "all" ? next.set("priority", filters.priority) : next.delete("priority");
    filters.dateRange !== "all" ? next.set("date", filters.dateRange) : next.delete("date");
    sort.by !== "modified" ? next.set("sort", sort.by) : next.delete("sort");
    sort.dir !== "desc" ? next.set("dir", sort.dir) : next.delete("dir");
    page > 1 ? next.set("page", String(page)) : next.delete("page");
    pageSize !== 10 ? next.set("pageSize", String(pageSize)) : next.delete("pageSize");

    if (next.toString() !== params.toString()) {
      setParams(next, { replace: true });
    }
  }, [filters, location.search, page, pageSize, params, q, setParams, showFilters, sort, viewMode]);

  const filteredRows = useMemo(() => {
    const term = q.trim().toLowerCase();
    let filtered = rows;

    if (term) {
      filtered = filtered.filter((row) =>
        [
          row.tail_number,
          row.broker_intake?.contact_name,
          row.broker_intake?.contact_email,
          row.broker_intake?.contact_phone,
          row.broker_intake?.airport_identifier,
          row.broker_intake?.services,
          row.broker_intake?.broker_accounts?.name,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term))
      );
    }

    if (filters.status !== "all") {
      filtered = filtered.filter((row) => normalizeStatus(row.current_status) === filters.status);
    }
    if (filters.priority !== "all") {
      filtered = filtered.filter((row) => row.priority === filters.priority);
    }
    if (filters.dateRange !== "all") {
      const now = new Date();
      let start = null;
      if (filters.dateRange === "30d") {
        start = new Date();
        start.setDate(now.getDate() - 30);
      } else if (filters.dateRange === "90d") {
        start = new Date();
        start.setDate(now.getDate() - 90);
      } else if (filters.dateRange === "year") {
        start = new Date(now.getFullYear(), 0, 1);
      }
      if (start) {
        filtered = filtered.filter((row) => {
          const createdAt = row?.broker_intake?.created_at;
          return createdAt && new Date(createdAt) >= start;
        });
      }
    }

    const factor = sort.dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      if (sort.by === "status") {
        return (
          (getStatusSortIndex(a.current_status) - getStatusSortIndex(b.current_status)) * factor
        );
      }
      if (sort.by === "priority") {
        return (PRIORITY_ORDER.indexOf(a.priority || "") - PRIORITY_ORDER.indexOf(b.priority || "")) * factor;
      }
      if (sort.by === "tail") {
        return String(a.tail_number || "").localeCompare(String(b.tail_number || "")) * factor;
      }
      if (sort.by === "broker") {
        return String(a.broker_intake?.broker_accounts?.name || "").localeCompare(
          String(b.broker_intake?.broker_accounts?.name || "")
        ) * factor;
      }
      if (sort.by === "requested") {
        const A = new Date(a.broker_intake?.created_at || 0).getTime();
        const B = new Date(b.broker_intake?.created_at || 0).getTime();
        return (A - B) * factor;
      }
      if (sort.by === "expected") {
        const aHasExpected = Boolean(a.expected_datetime);
        const bHasExpected = Boolean(b.expected_datetime);
        if (!aHasExpected && !bHasExpected) return 0;
        if (!aHasExpected) return 1;
        if (!bHasExpected) return -1;

        const A = new Date(a.expected_datetime).getTime();
        const B = new Date(b.expected_datetime).getTime();
        return (A - B) * factor;
      }
      const A = new Date(a.updated_at || a.broker_intake?.created_at || 0).getTime();
      const B = new Date(b.updated_at || b.broker_intake?.created_at || 0).getTime();
      return (A - B) * factor;
    });
  }, [rows, q, filters, sort]);

  const total = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = filteredRows.slice((safePage - 1) * pageSize, safePage * pageSize);
  const sortLabel = getSortLabel(sort);

  useEffect(() => {
    if (safePage !== page) setPage(safePage);
  }, [safePage, page]);

  const setFilterValue = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ status: "all", priority: "all", dateRange: "all" });
    setPage(1);
  };

  const showToast = (title, message) => {
    setToast({ title, message });
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setToast(null), 5000);
  };

  const openProject = (project) => {
    navigate(`/admin/projects/${project.id}`, {
      state: { from: `${location.pathname}${location.search}` },
    });
  };

  const deleteProject = async (project) => {
    const confirmed = window.confirm(`Delete project request for ${project.tail_number || "this tail"}? This cannot be undone.`);
    if (!confirmed) return;

    setOpenMenuId(null);
    const { error } = await supabase
      .from("broker_intake")
      .delete()
      .eq("id", project?.broker_intake?.id);

    if (error) {
      setErrorText(error.message || "Could not delete project.");
      return;
    }

    showToast("Deleted", `${project.tail_number || "Project"} was deleted.`);
    await loadRows();
  };

  const filterButtonLabel = getActiveFilterSummary(filters);

  return (
    <AdminDashboardLayout>
      <div className="space-y-6 text-[#1A1A1A]">
        {toast && (
          <Toast title={toast.title} message={toast.message} onClose={() => setToast(null)} />
        )}

        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight">Admin Projects</h1>
            <p className="text-xs text-black/60 mt-1">All projects • {sortLabel} • {total.toLocaleString()} total</p>
          </div>

          <div className="flex w-full flex-col items-center gap-3 sm:flex-row md:w-auto">
            <div className="flex h-10 overflow-hidden rounded-md border border-[#D9D9D9] text-sm font-medium">
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={`flex h-full items-center gap-1 px-4 transition ${
                  viewMode === "list" ? "bg-[#EB040D] text-white" : "bg-white text-black hover:bg-[#EB040D]/10"
                }`}
              >
                <List className="h-4 w-4" />
                List
              </button>
              <button
                type="button"
                onClick={() => setViewMode("card")}
                className={`flex h-full items-center gap-1 px-4 transition ${
                  viewMode === "card" ? "bg-[#EB040D] text-white" : "bg-white text-black hover:bg-[#EB040D]/10"
                }`}
              >
                <LayoutGrid className="h-4 w-4" />
                Cards
              </button>
            </div>

            <button
              type="button"
              onClick={() => setShowFilters((current) => !current)}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#D9D9D9] bg-white px-4 text-sm font-medium hover:bg-[#EB040D]/10"
            >
              <Filter className="h-4 w-4" />
              {filterButtonLabel}
            </button>

            <div className={`${PANEL} flex h-10 w-full items-center gap-2 px-3 sm:w-80`}>
              <Search className="h-4 w-4 shrink-0 text-black/35" strokeWidth={2} />
              <input
                value={q}
                onChange={(event) => {
                  setQ(event.target.value);
                  setPage(1);
                }}
                placeholder="Search tail, broker, contact"
                className="w-full bg-transparent text-sm outline-none placeholder-black/45"
              />
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin opacity-70" />
              ) : q ? (
                <button type="button" onClick={() => setQ("")} aria-label="Clear search">
                  <X className="h-4 w-4" />
                </button>
              ) : null}
            </div>
          </div>
        </div>

        {showFilters && (
          <div className={`${PANEL} p-4`}>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <FilterSelect label="Status" value={filters.status} onChange={(value) => setFilterValue("status", value)}>
                <option value="all">All statuses</option>
                {STATUS_ORDER.map((status) => <option key={status} value={status}>{status}</option>)}
              </FilterSelect>
              <FilterSelect label="Priority" value={filters.priority} onChange={(value) => setFilterValue("priority", value)}>
                <option value="all">All priorities</option>
                {PRIORITY_ORDER.map((priority) => <option key={priority} value={priority}>{priority}</option>)}
              </FilterSelect>
              <FilterSelect label="Requested Date" value={filters.dateRange} onChange={(value) => setFilterValue("dateRange", value)}>
                {DATE_FILTERS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </FilterSelect>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-black/45">Sort</label>
                <div className="grid grid-cols-[1fr_auto] gap-2">
                  <select
                    value={sort.by}
                    onChange={(event) => {
                      setSort({ by: event.target.value, dir: event.target.value === "modified" ? "desc" : "asc" });
                      setPage(1);
                    }}
                    className="w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm"
                  >
                    <option value="modified">Last Modified</option>
                    <option value="requested">Requested Date</option>
                    <option value="expected">Expected Date</option>
                    <option value="tail">Tail Number</option>
                    <option value="broker">Broker</option>
                    <option value="status">Status</option>
                    <option value="priority">Priority</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => setSort((current) => ({ ...current, dir: current.dir === "asc" ? "desc" : "asc" }))}
                    className="rounded-md border border-black/10 bg-white px-3 py-2 text-sm font-semibold hover:bg-black/[0.03]"
                  >
                    {sort.dir === "asc" ? "Asc" : "Desc"}
                  </button>
                </div>
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <button type="button" onClick={clearFilters} className="text-sm font-medium text-black/55 hover:text-black">
                Clear filters
              </button>
            </div>
          </div>
        )}

        {errorText && <PortalFeedback type="error">{errorText}</PortalFeedback>}

        {viewMode === "list" ? (
          <div className={`${PANEL} overflow-hidden`}>
            <div className="overflow-auto">
              <table className="w-full text-left">
                <tbody>
                  {loading && <tr><td className="py-8 text-center text-black/60">Loading…</td></tr>}
                  {!loading && pageRows.length === 0 && <tr><td className="py-8 text-center text-black/50">No projects found.</td></tr>}
                  {!loading && pageRows.map((project) => (
                    <ProjectRow
                      key={project.id}
                      project={project}
                      openMenuId={openMenuId}
                      setOpenMenuId={setOpenMenuId}
                      onOpen={openProject}
                      onDelete={deleteProject}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={safePage}
              pageSize={pageSize}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={(value) => {
                setPageSize(value);
                setPage(1);
              }}
            />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
              {loading && <div className="col-span-full py-8 text-center text-black/60">Loading…</div>}
              {!loading && pageRows.length === 0 && <div className="col-span-full py-8 text-center text-black/50">No projects found.</div>}
              {!loading && pageRows.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  onOpen={openProject}
                />
              ))}
            </div>
            <div className={`${PANEL} overflow-hidden`}>
              <Pagination
                page={safePage}
                pageSize={pageSize}
                total={total}
                totalPages={totalPages}
                onPageChange={setPage}
                onPageSizeChange={(value) => {
                  setPageSize(value);
                  setPage(1);
                }}
              />
            </div>
          </>
        )}
      </div>
    </AdminDashboardLayout>
  );
}

function ProjectRow({ project, openMenuId, setOpenMenuId, onOpen, onDelete }) {
  const intake = project.broker_intake;

  return (
    <tr onClick={() => onOpen(project)} className="cursor-pointer border-b border-black/10 transition hover:bg-black/[0.02]">
      <td className="px-6 py-5">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-[1fr_1.2fr_1fr]">
          <div className="space-y-2">
            <div className="text-sm font-semibold">
              Tail Number: <span className="font-bold">{project.tail_number || "—"}</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-black/50">Status:</span>
              <StatusPill status={project.current_status} />
            </div>
            <div className="text-xs text-black/60">
              Expected By: {formatExpectedTiming(project.expected_datetime, project.timezone)}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs">
              <span className="text-black/50">Priority:</span>
              <PriorityPill priority={project.priority} />
            </div>
            <div className="text-xs text-black/60">
              <span className="text-black/50">Broker:</span>{" "}
              <span className="font-medium text-black/75">{formatDisplayName(intake?.broker_accounts?.name) || "—"}</span>
            </div>
            <div className="text-xs leading-relaxed text-black/60">
              <span className="block text-black/50">Services:</span>
              {intake?.services || "—"}
            </div>
          </div>

          <div className="relative space-y-2 text-xs">
            <div>
              <span className="text-black/50">Contact:</span>{" "}
              <span className="font-medium">{formatDisplayName(intake?.contact_name) || "—"}</span>
            </div>
            <div>
              <span className="text-black/50">ICAO:</span> {intake?.airport_identifier || "—"}
            </div>
            <div>
              <span className="text-black/50">Requested:</span>{" "}
              {intake?.created_at ? new Date(intake.created_at).toLocaleDateString() : "—"}
            </div>
            <div>
              <span className="text-black/50">Assigned to:</span>{" "}
              <span className="font-medium">{formatDisplayName(project.assigned_pilot) || "—"}</span>
            </div>

            <ActionMenu
              project={project}
              open={openMenuId === project.id}
              onToggle={(event) => {
                event.stopPropagation();
                setOpenMenuId(openMenuId === project.id ? null : project.id);
              }}
              onEdit={(event) => {
                event.stopPropagation();
                setOpenMenuId(null);
                onOpen(project);
              }}
              onDelete={(event) => {
                event.stopPropagation();
                onDelete(project);
              }}
            />
          </div>
        </div>
      </td>
    </tr>
  );
}

function ProjectCard({ project, onOpen }) {
  const intake = project.broker_intake;

  return (
    <button
      type="button"
      onClick={() => onOpen(project)}
      className="min-h-[260px] rounded-xl border border-black/10 bg-white px-6 py-5 text-left shadow-sm transition hover:bg-black/[0.02]"
    >
      <div>
        <div className="text-sm font-bold">{project.tail_number || "—"}</div>
        <p className="mt-1 text-xs opacity-70">{formatDisplayName(intake?.broker_accounts?.name) || "Broker request"}</p>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <PriorityPill priority={project.priority} />
        <StatusPill status={project.current_status} />
      </div>
      <div className="mt-4 space-y-2 text-xs text-current/70">
        <p><strong>Services:</strong> {intake?.services || "—"}</p>
        <p><strong>Contact:</strong> {formatDisplayName(intake?.contact_name) || "—"} {intake?.contact_email ? `• ${intake.contact_email}` : ""}</p>
        <p><strong>ICAO:</strong> {intake?.airport_identifier || "—"}</p>
        <p><strong>Expected:</strong> {formatExpectedTiming(project.expected_datetime, project.timezone)}</p>
        <p><strong>Assigned:</strong> {formatDisplayName(project.assigned_pilot) || "—"}</p>
      </div>
    </button>
  );
}

function ActionMenu({ project, open, onToggle, onEdit, onDelete }) {
  return (
    <div className="admin-kebab-menu absolute -top-4 right-0">
      <button
        type="button"
        onClick={onToggle}
        className="inline-flex h-9 w-9 items-center justify-center rounded-md text-black hover:bg-black/[0.04]"
        aria-label={`Actions for ${project.tail_number}`}
      >
        <MoreHorizontal className="h-5 w-5" />
      </button>
      {open && (
        <div className="absolute right-0 z-10 mt-1 w-44 rounded-md border border-black/10 bg-white shadow-md">
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-black/5"
          >
            <Pencil className="h-4 w-4" />
            Edit / Schedule
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="inline-flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-black/5"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, children }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-black/45">{label}</label>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm"
      >
        {children}
      </select>
    </div>
  );
}

function Pagination({ page, pageSize, total, totalPages, onPageChange, onPageSizeChange }) {
  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div className="flex flex-col items-center justify-between gap-3 border-t border-black/10 bg-white/60 p-3 sm:flex-row">
      <div className="text-xs text-black/60">
        Page <span className="font-medium">{page}</span> of <span className="font-medium">{totalPages}</span> •{" "}
        <span className="font-medium">{total.toLocaleString()}</span> results
      </div>
      <div className="flex items-center gap-2">
        <select
          value={pageSize}
          onChange={(event) => onPageSizeChange(Number(event.target.value))}
          className="rounded-md border border-black/10 bg-white px-2 py-1 text-xs"
        >
          {[10, 20, 50].map((size) => <option key={size} value={size}>{size} / page</option>)}
        </select>
        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={!canPrev}
          className={`rounded-md border px-3 py-1 text-xs ${canPrev ? "border-black/20 hover:bg-black/[0.04]" : "cursor-not-allowed border-black/10 opacity-40"}`}
        >
          Prev
        </button>
        <button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={!canNext}
          className={`rounded-md border px-3 py-1 text-xs ${canNext ? "border-black/20 hover:bg-black/[0.04]" : "cursor-not-allowed border-black/10 opacity-40"}`}
        >
          Next
        </button>
      </div>
    </div>
  );
}

function Toast({ title, message, onClose }) {
  return (
    <div className="fixed right-4 top-4 z-50 w-[min(92vw,360px)] rounded-xl border border-black/10 bg-white p-4 shadow-2xl">
      <button
        type="button"
        onClick={onClose}
        className="absolute right-2 top-2 rounded p-1 text-black/50 hover:bg-black/5 hover:text-black"
        aria-label="Dismiss message"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex gap-3 pr-5">
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#EB040D]" />
        <div>
          <p className="text-sm font-semibold text-[#1A1A1A]">{title}</p>
          <p className="mt-1 text-sm text-black/65">{message}</p>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const normalized = normalizeStatus(status);
  const tone = STATUS_TONE[normalized] || "rgba(0,0,0,0.25)";

  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium sm:text-xs"
      style={{ color: tone, background: "rgba(0,0,0,0.035)", boxShadow: `inset 0 0 0 1px ${tone}22` }}
    >
      <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: tone }} />
      {normalized || "—"}
    </span>
  );
}

function PriorityPill({ priority }) {
  const bg = {
    Urgent: "rgba(211, 34, 43, 0.80)",
    Medium: "rgba(63, 63, 63, 0.80)",
    Low: "rgba(131, 131, 132, 0.80)",
  }[priority] || "rgba(0,0,0,0.20)";

  return (
    <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold text-white sm:text-xs" style={{ background: bg }}>
      {priority || "—"}
    </span>
  );
}
